/**
 * 电商套图 · 管理后台自定义模板 Tab（CRUD）
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { platformFetch } from '../../../platform/http';
import toast from '../../Toast';

interface SuiteTemplateRow {
  id: string;
  templateKey: string;
  name: string;
  keywords: string;
  promptScaffold: string;
  antiAiTips: string | null;
  supportsImageReference: boolean;
  enabled: boolean;
  sortOrder: number;
}

interface TemplateForm {
  templateKey: string;
  name: string;
  keywords: string;
  promptScaffold: string;
  antiAiTips: string;
  supportsImageReference: boolean;
  enabled: boolean;
}

const EMPTY_FORM: TemplateForm = {
  templateKey: 'custom_',
  name: '',
  keywords: '',
  promptScaffold: '',
  antiAiTips: '',
  supportsImageReference: true,
  enabled: true,
};

export default function EcommerceSuiteTemplatesTab() {
  const [templates, setTemplates] = useState<SuiteTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<TemplateForm | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await platformFetch('/admin/ecommerce-suite/templates');
      const data = await res.json();
      setTemplates(data.data || []);
    } catch {
      toast.error('获取模板列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const payload = {
        templateKey: editing.templateKey.trim(),
        name: editing.name.trim(),
        keywords: editing.keywords.split(/[,，\s]+/).filter(Boolean),
        promptScaffold: editing.promptScaffold,
        antiAiTips: editing.antiAiTips || null,
        supportsImageReference: editing.supportsImageReference,
        enabled: editing.enabled,
      };
      const res = await platformFetch(
        editingId ? `/admin/ecommerce-suite/templates/${editingId}` : '/admin/ecommerce-suite/templates',
        {
          method: editingId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.error || '保存失败');
      }
      toast.success(editingId ? '模板已更新' : '模板已创建');
      setEditing(null);
      setEditingId(null);
      await fetchTemplates();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('确定删除该模板？已生成的分镜不受影响。')) return;
    try {
      const res = await platformFetch(`/admin/ecommerce-suite/templates/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      toast.success('模板已删除');
      await fetchTemplates();
    } catch {
      toast.error('删除失败');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-light-muted">
          自定义模板与内置 25 种模板合并进入规划目录；标识需以 <code className="rounded bg-gray-100 px-1">custom_</code> 开头
        </p>
        <button
          type="button"
          onClick={() => {
            setEditing({ ...EMPTY_FORM });
            setEditingId(null);
          }}
          className="inline-flex items-center gap-1.5 rounded-xl bg-accent-blue px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          新建模板
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-light-border text-light-muted">
                <th className="px-4 py-3 text-left font-medium">标识</th>
                <th className="px-4 py-3 text-left font-medium">名称</th>
                <th className="px-4 py-3 text-left font-medium">关键词</th>
                <th className="px-4 py-3 text-left font-medium">状态</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-accent-blue mx-auto" /></td></tr>
              ) : templates.length === 0 ? (
                <tr><td colSpan={5} className="py-10 text-center text-light-muted">暂无自定义模板</td></tr>
              ) : (
                templates.map((template) => (
                  <tr key={template.id} className="border-b border-light-border/60 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-light-text">{template.templateKey}</td>
                    <td className="px-4 py-3 text-light-text">{template.name}</td>
                    <td className="px-4 py-3 text-light-muted text-xs max-w-[220px] truncate">
                      {(() => {
                        try { return JSON.parse(template.keywords).join(' / '); } catch { return '—'; }
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs ${template.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {template.enabled ? '启用' : '停用'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(template.id);
                            setEditing({
                              templateKey: template.templateKey,
                              name: template.name,
                              keywords: (() => { try { return JSON.parse(template.keywords).join(', '); } catch { return ''; } })(),
                              promptScaffold: template.promptScaffold,
                              antiAiTips: template.antiAiTips || '',
                              supportsImageReference: template.supportsImageReference,
                              enabled: template.enabled,
                            });
                          }}
                          className="rounded-lg p-1.5 text-light-muted transition hover:text-accent-blue"
                          title="编辑"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleDelete(template.id)} className="rounded-lg p-1.5 text-light-muted transition hover:text-red-500" title="删除">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button aria-label="关闭" className="absolute inset-0 bg-zinc-950/60" onClick={() => setEditing(null)} />
          <div className="relative max-h-[85vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl space-y-4">
            <h3 className="text-lg font-semibold text-light-text">{editingId ? '编辑模板' : '新建模板'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-light-text mb-1">模板标识（custom_ 开头）</label>
                <input
                  value={editing.templateKey}
                  onChange={(e) => setEditing({ ...editing, templateKey: e.target.value })}
                  disabled={!!editingId}
                  className="w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-light-text mb-1">名称</label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-light-text mb-1">关键词（逗号分隔，供 AI 选题参考）</label>
              <input
                value={editing.keywords}
                onChange={(e) => setEditing({ ...editing, keywords: e.target.value })}
                className="w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-light-text mb-1">prompt 骨架（可含 {'{product_description}'} 占位）</label>
              <textarea
                value={editing.promptScaffold}
                onChange={(e) => setEditing({ ...editing, promptScaffold: e.target.value })}
                className="h-28 w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue resize-y"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-light-text mb-1">反 AI 味提示（可选）</label>
              <input
                value={editing.antiAiTips}
                onChange={(e) => setEditing({ ...editing, antiAiTips: e.target.value })}
                className="w-full rounded-xl border border-light-border px-3 py-2 text-sm text-light-text outline-none focus:border-accent-blue"
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-light-text">
                <input type="checkbox" checked={editing.supportsImageReference} onChange={(e) => setEditing({ ...editing, supportsImageReference: e.target.checked })} />
                支持参考图
              </label>
              <label className="flex items-center gap-2 text-sm text-light-text">
                <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                启用
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="rounded-xl border border-light-border px-4 py-2 text-sm text-light-text">取消</button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-accent-blue px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
